import { useState } from 'react'
import { useSnackbar } from 'notistack'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Tooltip } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import EditIcon from '@mui/icons-material/Edit'

import { useUpdatePlanningMutation } from '@/core/services/planning'
import { getApiErrorMessage } from '@/utils/errors'
import { Planning } from '@/core/models/planning.model'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

import { PlanningForm, PlanningFormData } from './PlanningForm'
import { serializePlanningDateRange } from '../lib/planningDates'

const FORM_ID = 'edit-planning-form'

type EditPlanningDialogProps = {
  planning: Planning
  onSuccess?: () => void
}

export function EditPlanningDialog({ planning, onSuccess }: EditPlanningDialogProps) {
  useLingui()
  const { enqueueSnackbar } = useSnackbar()
  const isMobile = useIsMobile()

  const [openDialog, setOpenDialog] = useState(false)

  const [updatePlanning, { isLoading: isUpdatingPlanning }] = useUpdatePlanningMutation()

  const defaultValues = {
    name: planning.name,
    description: planning.description || '',
    dateRange: {
      start: new Date(planning.startDate),
      end: new Date(planning.endDate),
    },
  }

  const handleSubmit = async (data: PlanningFormData) => {
    try {
      await updatePlanning({
        planningId: planning._id,
        updatePlanningParams: {
          name: data.name,
          description: data.description,
          ...serializePlanningDateRange(data.dateRange.start, data.dateRange.end),
        },
      }).unwrap()
      enqueueSnackbar(t`Planning updated successfully`, { variant: 'success' })
      setOpenDialog(false)
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t`Failed to update planning`), { variant: 'error' })
    }
  }

  return (
    <>
      <Tooltip title={t`Edit Planning`}>
        <IconButton onClick={() => setOpenDialog(true)} size="small" color="primary">
          <EditIcon />
        </IconButton>
      </Tooltip>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullScreen={isMobile} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Trans>Edit Planning</Trans>
          <Tooltip title={t`Close`}>
            <IconButton onClick={() => setOpenDialog(false)}>
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          <PlanningForm formId={FORM_ID} onSubmit={handleSubmit} defaultValues={defaultValues} isEditMode />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <Button color="primary" disabled={isUpdatingPlanning} form={FORM_ID} type="submit" variant="contained">
            <Trans>Save</Trans>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
